import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';

import { Button } from '#/modules/commonComponents/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import { cn } from '#/lib/utils';
import OutboundQtyStat from '#/modules/momMgmt/workOrder/outbound/components/OutboundQtyStat';
import WorkOrderOutboundActionHintIcon from '#/modules/momMgmt/workOrder/outbound/components/WorkOrderOutboundActionHintIcon';
import { buildWorkOrderCreateUrlFromRequirement } from '#/modules/momMgmt/workOrder/helper/buildWorkOrderCreateUrlFromRequirement';
import { buildWorkOrderTransferCreateUrl } from '#/modules/momMgmt/workOrder/helper/buildWorkOrderTransferCreateUrl';
import {
  getMtrNoPrefix,
  isWorkOrderCreatePrefix,
} from '#/modules/momMgmt/workOrder/helper/materialRequirementPrefix';
import { getOutboundRemainingQtyValueClass } from '#/modules/momMgmt/workOrder/outbound/helper/outboundQtyStatClasses';

const SHORTAGE_HINT = '這個工作倉目前沒有這項物料的庫存，無法選擇出庫來源。';

function displayQty(value) {
  if (value == null || value === '') {
    return '—';
  }
  return String(value);
}

function materialRowClassName({ hasDraft, focused, isShortage }) {
  if (focused) {
    return 'bg-primary-200/65 hover:bg-primary-200/65';
  }
  if (hasDraft) {
    return 'bg-primary-50/60 hover:bg-primary-50';
  }
  if (isShortage) {
    return 'bg-slate-50/80';
  }
  return 'hover:bg-slate-50';
}

function stopRowClick(event) {
  event.stopPropagation();
}

const WorkOrderOutboundMaterialList = ({
  materials = [],
  selectedCountByMtrId = {},
  focusedMtrId = null,
  focusToken = 0,
  workWarehouseId = null,
  onOpenSourcePicker,
  onFocusMaterial,
  emptyHint,
  disabled = false,
}) => {
  const rowRefs = useRef({});

  useEffect(() => {
    if (!focusedMtrId) {
      return;
    }
    const node = rowRefs.current[focusedMtrId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusedMtrId, focusToken, materials]);

  if (materials.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-sm text-muted-foreground">
        {emptyHint || '尚無可出庫候選物料。'}
      </p>
    );
  }

  const transferCreateUrl = buildWorkOrderTransferCreateUrl({
    warehouseId: workWarehouseId,
  });

  return (
    <ul className="m-0 list-none divide-y divide-slate-100 rounded-md border border-slate-200 p-0">
      {materials.map((material) => {
        const selectedCount = selectedCountByMtrId[material.mtrId] ?? 0;
        const hasSelected = selectedCount > 0;
        const focused = focusedMtrId === material.mtrId;
        const isShortage = material.isShortage ?? !(material.sources?.length > 0);
        const prefix = getMtrNoPrefix(material.mtrNo);
        const showWorkOrderCreate = isWorkOrderCreatePrefix(prefix);
        const workOrderCreateUrl = buildWorkOrderCreateUrlFromRequirement(material);

        return (
          <li
            key={material.key}
            ref={(node) => {
              if (!material.mtrId) {
                return;
              }
              if (node) {
                rowRefs.current[material.mtrId] = node;
              } else {
                delete rowRefs.current[material.mtrId];
              }
            }}
            className={cn(
              'flex flex-wrap items-center gap-3 px-3 py-3 transition-colors',
              isShortage ? 'cursor-default' : 'cursor-pointer',
              materialRowClassName({ hasDraft: hasSelected, focused, isShortage }),
            )}
            onClick={() => {
              if (!isShortage) {
                onFocusMaterial?.(material.mtrId);
              }
            }}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-800">
                {material.mtrName || '（未命名）'}
                {isShortage ? (
                  <span className="ml-2 font-normal text-amber-800">{"(缺料)"}</span>
                ) : null}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {material.mtrNo || material.mtrId}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <div className="flex flex-wrap justify-end gap-3">
                <OutboundQtyStat
                  label="需求"
                  value={displayQty(material.requiredQty)}
                />
                <OutboundQtyStat
                  label="剩餘"
                  value={displayQty(material.remainingQty)}
                  valueClassName={getOutboundRemainingQtyValueClass(material.remainingQty)}
                />
                <OutboundQtyStat
                  label="已選"
                  value={selectedCount}
                  valueWidth="count"
                  suffix="筆"
                />
              </div>
              {isShortage ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    disabled
                    onClick={stopRowClick}
                  >
                    <span className="inline-flex items-center gap-2">
                      {"(缺料)"}
                      <WorkOrderOutboundActionHintIcon
                        tip={SHORTAGE_HINT}
                        ariaLabel="缺料說明"
                        tone="inherit"
                      />
                    </span>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={stopRowClick}
                      >
                        其他操作
                        <ChevronDown className="size-3.5" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-auto min-w-44"
                      onClick={stopRowClick}
                    >
                      <DropdownMenuItem asChild>
                        <a
                          href={transferCreateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          新增工單轉倉
                        </a>
                      </DropdownMenuItem>
                      {showWorkOrderCreate ? (
                        <DropdownMenuItem asChild>
                          <a
                            href={workOrderCreateUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            建立工單
                          </a>
                        </DropdownMenuItem>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenSourcePicker(material);
                  }}
                >
                  選擇來源
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

WorkOrderOutboundMaterialList.propTypes = {
  materials: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    requiredQty: PropTypes.number,
    remainingQty: PropTypes.number,
    isShortage: PropTypes.bool,
    sources: PropTypes.arrayOf(PropTypes.object),
  })),
  selectedCountByMtrId: PropTypes.objectOf(PropTypes.number),
  focusedMtrId: PropTypes.string,
  focusToken: PropTypes.number,
  workWarehouseId: PropTypes.string,
  onOpenSourcePicker: PropTypes.func.isRequired,
  onFocusMaterial: PropTypes.func,
  emptyHint: PropTypes.string,
  disabled: PropTypes.bool,
};

export default WorkOrderOutboundMaterialList;
